/**
 * L'application du chauffeur : ce qu'il a à faire, et ce qu'il déclare avoir livré.
 *
 * *Demande de Chris, 16/09/2026 : « le chauffeur doit avoir une application pour voir ce qu'il a à
 * faire et valider chaque fois qu'il a livré. »*
 *
 * ## Il pointe une pastille à la fois
 *
 * Contrairement au chef, qui déclare sa journée le soir en une fois. Le chauffeur valide *« chaque
 * fois qu'il a livré »* : un bouton par ligne, un envoi par ligne, et le compte se met à jour
 * tout de suite. Attendre la fin de la journée lui ferait ressaisir de mémoire son premier voyage.
 *
 * ## Il compte des VOYAGES, pas des tonnes
 *
 * Quand la charge utile du couple d'attelage est connue, l'écran met des boutons **+1 voyage** et
 * écrit le calcul en toutes lettres — « 3 voyages × 26 T = 78 T ». Un tonnage seul ne dit pas
 * lequel des deux termes corriger, c'est la leçon du contrôle du transport. Hors tonnage, saisie
 * directe dans l'unité de la ligne.
 *
 * ## Le convoi est visible
 *
 * Une livraison de 700 tonnes se fait à plusieurs camions. L'écran montre ce que **tous** ont
 * déclaré, pas seulement soi : un chauffeur qui ne verrait que son propre total ne saurait jamais
 * s'il reste des voyages à faire.
 *
 * ## Conçu pour le pouce
 *
 * C'est le seul écran du produit qui sera utilisé debout, dehors, à côté d'un camion. Boutons
 * larges, une action par ligne, aucun survol, aucun tableau : des cartes à toutes les tailles
 * d'écran — même sur un PC, parce que cet écran-là ne s'ouvre pas sur un PC.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { ajouterJours, formaterDate, formaterNombre } from "@planif/moteur";
import {
  adresseDuBon,
  annulerLePointage,
  chargerJourneeChauffeur,
  deposerUnBon,
  pointerUneLivraison,
  supprimerUnBon,
} from "./api";
import type { JourneeDuChauffeur, LivraisonAPointer } from "./api";

/**
 * Ce que valent des voyages, pour l'aperçu à l'écran.
 *
 * **L'écran affiche, le serveur enregistre.** La même multiplication vit aux deux endroits, ce
 * qui se discute — mais l'alternative était pire : envoyer une requête à chaque appui sur « +1 »
 * pour connaître le total. La valeur qui est stockée reste celle du serveur, et l'écran la relit
 * après chaque envoi ; s'il se trompait, l'écart se verrait au premier pointage.
 */
export function apercuDesVoyages(voyages: number, chargeUtile: string | null): string | null {
  if (chargeUtile === null || chargeUtile.trim() === "") return null;
  const charge = Number(chargeUtile);
  if (!Number.isFinite(charge) || charge <= 0) return null;
  return (charge * voyages).toString();
}

/** Ce qu'il reste à livrer sur une pastille, ou `null` si c'est incalculable. */
export function resteALivrer(livraison: LivraisonAPointer): number | null {
  const prevu = Number(livraison.quantitePrevue);
  const fait = Number(livraison.totalPointe);
  if (!Number.isFinite(prevu) || !Number.isFinite(fait)) return null;
  return prevu - fait;
}

export function SaisieChauffeur(): JSX.Element {
  const [jour, setJour] = useState<string>("");
  const [pour, setPour] = useState<string>("");
  const [journee, setJournee] = useState<JourneeDuChauffeur | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  /** Le brouillon par pastille : voyages en cours de comptage, ou quantité saisie. */
  const [brouillons, setBrouillons] = useState<
    Map<string, { voyages: number; quantite: string; remarque: string }>
  >(new Map());
  const [enCours, setEnCours] = useState<string | null>(null);
  /**
   * Le tonnage lu sur le bon qu'on est en train de photographier, par pastille.
   *
   * *« Quand la photo est prise, le champ qté se remplit » (Chris, 16/09/2026).* Le chauffeur
   * relève la pesée sur le bon, puis déclenche l'appareil : les deux partent ensemble. Vidé après
   * l'envoi, parce que le bon suivant portera un autre tonnage.
   */
  const [peseeDuBon, setPeseeDuBon] = useState<Map<string, string>>(new Map());

  const charger = useCallback(async (options: { jour?: string; pour?: string }) => {
    setChargement(true);
    setErreur(null);
    try {
      const lu = await chargerJourneeChauffeur(options);
      setJournee(lu);
      setJour(lu.jour);
      setBrouillons(
        new Map(
          lu.livraisons.map((l) => [
            l.livraisonId,
            {
              voyages: l.monPointage?.voyages ?? 0,
              quantite: l.monPointage?.quantite ?? "",
              remarque: l.monPointage?.remarque ?? "",
            },
          ]),
        ),
      );
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
      setJournee(null);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger({});
  }, [charger]);

  const modifier = (
    livraisonId: string,
    applique: (brouillon: { voyages: number; quantite: string; remarque: string }) => void,
  ): void => {
    setBrouillons((precedent) => {
      const copie = new Map(precedent);
      const actuel = copie.get(livraisonId) ?? { voyages: 0, quantite: "", remarque: "" };
      const neuf = { ...actuel };
      applique(neuf);
      copie.set(livraisonId, neuf);
      return copie;
    });
  };

  const pointer = async (livraison: LivraisonAPointer): Promise<void> => {
    const brouillon = brouillons.get(livraison.livraisonId);
    if (brouillon === undefined) return;
    setEnCours(livraison.livraisonId);
    setErreur(null);
    setMessage(null);
    try {
      await pointerUneLivraison({
        livraisonId: livraison.livraisonId,
        jour,
        pour: pour === "" ? undefined : pour,
        libelle: livraison.libelle,
        voyages: livraison.chargeUtile !== null && brouillon.voyages > 0 ? brouillon.voyages : undefined,
        quantite: livraison.chargeUtile === null ? brouillon.quantite : undefined,
        chargeUtile: livraison.chargeUtile ?? undefined,
        remarque: brouillon.remarque === "" ? undefined : brouillon.remarque,
      });
      setMessage(`${livraison.libelle} : pointage enregistré.`);
      await charger({ jour, pour });
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setEnCours(null);
    }
  };

  const annuler = async (livraison: LivraisonAPointer): Promise<void> => {
    setEnCours(livraison.livraisonId);
    try {
      await annulerLePointage({
        livraisonId: livraison.livraisonId,
        jour,
        pour: pour === "" ? undefined : pour,
      });
      setMessage(`${livraison.libelle} : pointage annulé.`);
      await charger({ jour, pour });
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setEnCours(null);
    }
  };

  /**
   * Photographier un bon, avec sa pesée.
   *
   * **Il faut un pointage d'abord** : le bon se rattache à lui. Plutôt que de refuser, on en crée
   * un à zéro — le chauffeur qui photographie son premier bon n'a pas encore compté ses voyages,
   * et lui demander de valider une quantité vide avant de pouvoir prendre la photo serait
   * exactement la friction qu'on cherche à supprimer.
   */
  const photographierLeBon = async (
    livraison: LivraisonAPointer,
    fichier: File,
  ): Promise<void> => {
    setEnCours(livraison.livraisonId);
    setErreur(null);
    setMessage(null);
    try {
      let pointageId = livraison.monPointage?.id ?? null;
      if (pointageId === null) {
        await pointerUneLivraison({
          livraisonId: livraison.livraisonId,
          jour,
          pour: pour === "" ? undefined : pour,
          libelle: livraison.libelle,
          quantite: "0",
        });
        const relu = await chargerJourneeChauffeur({ jour, pour });
        pointageId =
          relu.livraisons.find((l) => l.livraisonId === livraison.livraisonId)?.monPointage?.id ??
          null;
      }
      if (pointageId === null) throw new Error("Le pointage n'a pas pu être ouvert.");

      await deposerUnBon(pointageId, fichier, {
        quantite: peseeDuBon.get(livraison.livraisonId) ?? "",
        // Le code ERP et le libellé viennent de la ligne de contre-étude : c'est l'écran qui les
        // connaît, et ils voyagent avec la photo pour se retrouver dans la feuille du chef.
        codeErp: livraison.codeErp ?? undefined,
        libelle: livraison.libelle,
      });
      setPeseeDuBon((precedent) => {
        const copie = new Map(precedent);
        copie.delete(livraison.livraisonId);
        return copie;
      });
      setMessage(`Bon enregistré pour ${livraison.libelle}.`);
      await charger({ jour, pour });
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setEnCours(null);
    }
  };

  const retirerLeBon = async (bonId: string): Promise<void> => {
    setErreur(null);
    try {
      await supprimerUnBon(bonId);
      await charger({ jour, pour });
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const allerAu = (decalage: number): void => {
    const cible = ajouterJours(jour === "" ? (journee?.jour ?? "") : jour, decalage);
    setJour(cible);
    void charger({ jour: cible, pour });
  };

  return (
    <section className="chauffeur">
      <div className="chauffeur__barre">
        <button type="button" onClick={() => { allerAu(-1); }} disabled={chargement}>
          ← veille
        </button>
        <input
          type="date"
          value={jour}
          onChange={(e) => {
            setJour(e.target.value);
            void charger({ jour: e.target.value, pour });
          }}
        />
        <button type="button" onClick={() => { allerAu(1); }} disabled={chargement}>
          lendemain →
        </button>
        {journee?.estAdmin === true && (
          <select
            className="chauffeur__pour"
            value={pour}
            onChange={(e) => {
              setPour(e.target.value);
              void charger({ jour, pour: e.target.value });
            }}
          >
            <option value="">moi-même</option>
            {journee.chauffeursPossibles.map((c) => (
              <option key={c.salarieId} value={c.salarieId}>
                {c.libelle}
              </option>
            ))}
          </select>
        )}
      </div>

      {erreur !== null && <p className="erreur">{erreur}</p>}
      {message !== null && <p className="alerte-douce">{message}</p>}
      {chargement && <p>Lecture de la tournée…</p>}

      {!chargement && journee !== null && journee.rotations.length > 0 && (
        <section className="chauffeur__bloc">
          <h3>Ce qui est prévu</h3>
          {/* En lecture seule : c'est le plan, pas une déclaration. Le chauffeur ne corrige
              pas son planning depuis ici — c'est le conducteur qui le fait. */}
          <ul className="chauffeur__rotations">
            {journee.rotations.map((rotation, rang) => (
              <li key={`${rotation.ouvrage}#${String(rang)}`}>
                {/* **L'AFFAIRE d'abord** (Chris, 16/09/2026) : un chauffeur qui roule sur deux
                    chantiers dans la journée ne peut rien faire de « 1.3 MOI CHAUFFEUR ». */}
                <strong>{rotation.apo}</strong> · nœud {rotation.ouvrage} · {rotation.libelle}
                {rotation.trajet !== null && <span> · {rotation.trajet}</span>}
                {rotation.km !== null && <span> · {formaterNombre(rotation.km, 0)} km</span>}
                {rotation.heureLimite !== null && <span> · avant {rotation.heureLimite}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!chargement && journee !== null && journee.livraisons.length === 0 && (
        <p className="chauffeur__vide">
          Rien à pointer le {formaterDate(journee.jour)}. Cet écran ne montre que les matériaux
          que <strong>nos camions</strong> vont chercher : ce qu'un fournisseur livre lui-même est
          confirmé par le chef de chantier, pas ici.
        </p>
      )}

      {!chargement &&
        journee !== null &&
        journee.livraisons.map((livraison) => {
          const brouillon = brouillons.get(livraison.livraisonId) ?? {
            voyages: 0,
            quantite: "",
            remarque: "",
          };
          const apercu =
            livraison.chargeUtile === null
              ? null
              : apercuDesVoyages(brouillon.voyages, livraison.chargeUtile);
          const reste = resteALivrer(livraison);
          const occupe = enCours === livraison.livraisonId;

          return (
            <article key={livraison.livraisonId} className="chauffeur__livraison">
              <header>
                <h3>{livraison.libelle}</h3>
                <p className="chauffeur__ou">
                  {livraison.apo} · nœud {livraison.ouvrage}
                  {livraison.villeLivraison !== null && <> · {livraison.villeLivraison}</>}
                </p>
                <p className="chauffeur__provenance">
                  {livraison.provenance}
                  {livraison.heureLimite !== null && <> · attendu avant {livraison.heureLimite}</>}
                </p>
              </header>

              <p className="chauffeur__compte">
                Prévu <strong>{formaterNombre(livraison.quantitePrevue, 2)} {livraison.unite}</strong>
                {" · déjà pointé "}
                <strong>{formaterNombre(livraison.totalPointe, 2)} {livraison.unite}</strong>
                {/* Le reste à livrer est une QUANTITÉ, jamais une case « fait » (règle du
                    03/09) : c'est ce qui permet au chauffeur suivant de savoir quoi faire. */}
                {reste !== null && reste > 0 && (
                  <> · <span className="chauffeur__reste">reste {formaterNombre(reste.toString(), 2)} {livraison.unite}</span></>
                )}
                {reste !== null && reste < 0 && (
                  <> · <span className="en-ecart">dépassement de {formaterNombre((-reste).toString(), 2)} {livraison.unite}</span></>
                )}
              </p>

              {livraison.autresChauffeurs > 0 && (
                <p className="chauffeur__convoi">
                  {livraison.autresChauffeurs} autre(s) chauffeur(s) ont pointé cette livraison.
                </p>
              )}

              {livraison.chargeUtile === null ? (
                <label className="chauffeur__champ">
                  Quantité livrée ({livraison.unite})
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={brouillon.quantite}
                    onChange={(e) => {
                      modifier(livraison.livraisonId, (b) => {
                        b.quantite = e.target.value;
                      });
                    }}
                  />
                </label>
              ) : (
                <div className="chauffeur__voyages">
                  <button
                    type="button"
                    className="chauffeur__moins"
                    disabled={brouillon.voyages <= 0}
                    onClick={() => {
                      modifier(livraison.livraisonId, (b) => {
                        b.voyages = Math.max(0, b.voyages - 1);
                      });
                    }}
                  >
                    −
                  </button>
                  <span className="chauffeur__nombre">
                    {brouillon.voyages} voyage{brouillon.voyages > 1 ? "s" : ""}
                  </span>
                  <button
                    type="button"
                    className="chauffeur__plus"
                    onClick={() => {
                      modifier(livraison.livraisonId, (b) => {
                        b.voyages += 1;
                      });
                    }}
                  >
                    +1 voyage
                  </button>
                  {/* Le calcul écrit en toutes lettres : un tonnage seul ne dit pas lequel des
                      deux termes corriger si le compte est faux. */}
                  {apercu !== null && (
                    <span className="chauffeur__calcul">
                      {brouillon.voyages} × {formaterNombre(livraison.chargeUtile, 2)}{" "}
                      {livraison.unite} = <strong>{formaterNombre(apercu, 2)} {livraison.unite}</strong>
                    </span>
                  )}
                </div>
              )}

              {/*
                **Le bon de la carrière : la pesée puis la photo, dans le même geste.**

                *« Quand la photo est prise, le champ qté se remplit, avec aussi le champ code ERP
                du matériau et libellé » (Chris, 16/09/2026).* Le chauffeur relève le tonnage
                inscrit sur le bon, déclenche l'appareil, et la ligne part entière : pesée, code,
                libellé, image.

                `capture="environment"` ouvre l'appareil arrière directement sur un téléphone, et
                reste un simple choix de fichier sur un PC — le même bouton sert les deux.
              */}
              <div className="chauffeur__bons">
                <label className="chauffeur__champ">
                  Tonnage inscrit sur le bon ({livraison.unite})
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={peseeDuBon.get(livraison.livraisonId) ?? ""}
                    onChange={(e) => {
                      setPeseeDuBon((precedent) => {
                        const copie = new Map(precedent);
                        copie.set(livraison.livraisonId, e.target.value);
                        return copie;
                      });
                    }}
                  />
                </label>
                <label className="chauffeur__photo">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    disabled={occupe}
                    onChange={(e) => {
                      const fichier = e.target.files?.[0];
                      e.target.value = "";
                      if (fichier !== undefined) void photographierLeBon(livraison, fichier);
                    }}
                  />
                  <span>📷 Photo du bon</span>
                </label>

                {(livraison.monPointage?.bons ?? []).length > 0 && (
                  <ul className="chauffeur__listeBons">
                    {(livraison.monPointage?.bons ?? []).map((bon, rang) => (
                      <li key={bon.id}>
                        Bon {rang + 1}
                        {bon.quantite === null ? (
                          <span className="chauffeur__sansPesee"> · tonnage non relevé</span>
                        ) : (
                          <>
                            {" · "}
                            <strong>
                              {formaterNombre(bon.quantite, 2)} {livraison.unite}
                            </strong>
                          </>
                        )}
                        {bon.codeErp !== null && <> · {bon.codeErp}</>}{" "}
                        <a href={adresseDuBon(bon.id)} target="_blank" rel="noreferrer">
                          voir
                        </a>{" "}
                        <button
                          type="button"
                          className="chauffeur__retirerBon"
                          onClick={() => { void retirerLeBon(bon.id); }}
                        >
                          retirer
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {livraison.monPointage?.sourceQuantite === "bons" && (
                  <p className="chauffeur__note">
                    La journée retient{" "}
                    <strong>
                      {formaterNombre(livraison.monPointage.quantiteRetenue, 2)} {livraison.unite}
                    </strong>{" "}
                    — la somme des bons pesés, et non le comptage de voyages.
                  </p>
                )}
              </div>

              <label className="chauffeur__champ">
                Remarque
                <input
                  type="text"
                  value={brouillon.remarque}
                  onChange={(e) => {
                    modifier(livraison.livraisonId, (b) => {
                      b.remarque = e.target.value;
                    });
                  }}
                />
              </label>

              <div className="chauffeur__actions">
                <button
                  type="button"
                  className="chauffeur__valider"
                  disabled={occupe}
                  onClick={() => { void pointer(livraison); }}
                >
                  {livraison.monPointage === null ? "J'ai livré" : "Corriger mon pointage"}
                </button>
                {livraison.monPointage !== null && (
                  <button
                    type="button"
                    disabled={occupe}
                    onClick={() => { void annuler(livraison); }}
                  >
                    Annuler
                  </button>
                )}
              </div>

              {livraison.monPointage !== null && (
                <p className="chauffeur__mien">
                  Vous avez déclaré{" "}
                  <strong>
                    {formaterNombre(livraison.monPointage.quantite, 2)} {livraison.unite}
                  </strong>
                  {livraison.monPointage.voyages !== null && (
                    <> ({livraison.monPointage.voyages} voyage(s))</>
                  )}
                  .
                </p>
              )}
            </article>
          );
        })}
    </section>
  );
}
